import { useId, useState, type FormEvent } from "react";
import type { RateItem } from "@/api/clients";
import {
  isOneOff,
  type DiscountInput,
  type InvoiceUpdateInput,
  type LineInput,
} from "@/api/invoices";
import type { BankAccount } from "@/api/settings";
import type { VesselCall } from "@/api/vessels";
import { Plus } from "lucide-react";
import { RequestError } from "@/components/RequestError";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { SelectField, TextAreaField, TextField, type SelectOption } from "@/components/ui/field";
import { FormActions } from "@/components/ui/form-actions";
import { Input } from "@/components/ui/input";
import { addDays } from "@/lib/dates";
import { formatDate, formatMoney } from "@/lib/format";
import { centsToAmount, lineCents } from "@/lib/money";
import { useFormValues, useSubmitState } from "@/lib/use-form";

export type DraftInitial = {
  vessel_call_id: string;
  invoice_date: string;
  payment_days: number;
  bank_account_id: string | null;
  remarks: string;
  lines: LineInput[];
  discount?: DiscountInput | null;
  extra_notes?: string[];
};

/** One note per line of the text box; blank lines are dropped. */
export function notesFromText(text: string): string[] {
  return text
    .split("\n")
    .map((line) => line.trim())
    .filter((line) => line !== "");
}

export const STANDARD_DISCOUNT_NOTE =
  "Leave blank for the standard wording: \u201cDISCOUNT: \u2026 deducted in full as a one-time " +
  "goodwill concession limited to this invoice\u2026\u201d. Write your own when that does not fit, " +
  "e.g. a part discount for a delay.";

export const EXTRA_NOTES_HINT =
  "Printed after the standard notes (tax, agreement, disputes), numbered on. One note per line.";

/** A rate card item on this invoice: its quantity and the wording printed for it, which
 *  starts as the card's and can be changed for this invoice alone. */
type LineChoice = {
  checked: boolean;
  qty: string;
  description: string;
  detail: string;
  rewording: boolean;
};

/** A charge agreed for this call alone, typed in rather than picked off the rate card. */
type Extra = {
  key: string;
  description: string;
  detail: string;
  qty: string;
  unit_rate: string;
  /** Kept from a saved draft; a new charge is numbered and grouped by the server. */
  rate_group: "A" | "B";
  item_no?: number;
};

let nextExtraKey = 0;

function blankExtra(): Extra {
  nextExtraKey += 1;
  return {
    key: `extra-${nextExtraKey}`,
    description: "",
    detail: "",
    qty: "1",
    unit_rate: "",
    rate_group: "B",
  };
}

/** Typed lines already on a draft, so editing one does not lose them. */
function initialExtras(lines: LineInput[]): Extra[] {
  return lines.filter(isOneOff).map((line) => ({
    ...blankExtra(),
    description: line.description,
    detail: line.detail ?? "",
    qty: line.qty,
    unit_rate: line.unit_rate,
    rate_group: line.rate_group ?? "B",
    item_no: line.item_no,
  }));
}

const GROUP_TITLES = {
  A: "Group A - billed on every vessel call",
  B: "Group B - only when requested",
} as const;

function sortItems(items: RateItem[]): RateItem[] {
  return [...items].sort(
    (a, b) =>
      a.rate_group.localeCompare(b.rate_group) ||
      a.sort_order - b.sort_order ||
      a.item_no - b.item_no,
  );
}

function cardChoice(item: RateItem): LineChoice {
  return { checked: false, qty: "1", description: item.description, detail: item.detail, rewording: false };
}

function initialChoices(items: RateItem[], lines: LineInput[]): Record<string, LineChoice> {
  const chosen = new Map(lines.flatMap((line) => (isOneOff(line) ? [] : [[line.rate_item_id, line]])));
  return Object.fromEntries(
    items.map((item) => {
      const line = chosen.get(item.id);
      if (!line) return [item.id, cardChoice(item)];
      const description = line.description ?? item.description;
      const detail = line.detail ?? item.detail;
      const reworded = description !== item.description || detail !== item.detail;
      return [
        item.id,
        { checked: true, qty: String(Number(line.qty)), description, detail, rewording: reworded },
      ];
    }),
  );
}

/** The rate card line as sent: the wording only where it differs from the card's. */
function cardLine(item: RateItem, choice: LineChoice): LineInput {
  const description = choice.description.trim();
  const detail = choice.detail.trim();
  return {
    rate_item_id: item.id,
    qty: choice.qty.trim(),
    ...(description && description !== item.description ? { description } : {}),
    ...(detail !== item.detail ? { detail } : {}),
  };
}

export function callLabel(call: VesselCall): string {
  const voyage = call.voyage ? ` · voyage ${call.voyage}` : "";
  return `${call.vessel_name} · departs ${formatDate(call.departure)}${voyage}`;
}

type InvoiceDraftFormProps = {
  /** The client's vessel calls that are not billed on another invoice. */
  calls: VesselCall[];
  /** The client's active rate items. */
  rateItems: RateItem[];
  bankAccounts: BankAccount[];
  currency: string;
  initial: DraftInitial;
  submitLabel: string;
  onSubmit: (input: InvoiceUpdateInput) => Promise<unknown>;
  onCancel?: () => void;
  onReload?: () => void;
};

/** A draft's vessel call, dates, bank account and rate-card items, with a running total. */
export function InvoiceDraftForm({
  calls,
  rateItems,
  bankAccounts,
  currency,
  initial,
  submitLabel,
  onSubmit,
  onCancel,
  onReload,
}: InvoiceDraftFormProps) {
  const id = useId();
  const items = sortItems(rateItems);
  const { values, set } = useFormValues({
    vessel_call_id: initial.vessel_call_id,
    invoice_date: initial.invoice_date,
    payment_days: String(initial.payment_days),
    bank_account_id: initial.bank_account_id ?? "",
    remarks: initial.remarks,
    discount_amount: initial.discount ? String(Number(initial.discount.amount)) : "",
    discount_description: initial.discount?.description ?? "",
    discount_note: initial.discount?.note ?? "",
    extra_notes: (initial.extra_notes ?? []).join("\n"),
  });
  const [choices, setChoices] = useState(() => initialChoices(items, initial.lines));
  const [extras, setExtras] = useState<Extra[]>(() => initialExtras(initial.lines));
  const [missing, setMissing] = useState<Record<string, string>>({});
  const { error, errors, saving, run } = useSubmitState();

  const chosen = items.flatMap((item) => {
    const choice = choices[item.id];
    return choice?.checked ? [{ item, qty: choice.qty, choice }] : [];
  });
  const filledExtras = extras.filter((extra) => extra.description.trim() && extra.unit_rate.trim());
  const subtotalCents =
    chosen.reduce((sum, line) => sum + lineCents(line.qty, line.item.unit_rate), 0) +
    filledExtras.reduce((sum, extra) => sum + lineCents(extra.qty, extra.unit_rate), 0);
  const discountCents = lineCents("1", values.discount_amount);
  const totalCents = subtotalCents - discountCents;
  const days = Number.parseInt(values.payment_days, 10);
  const dueDate = values.invoice_date && Number.isFinite(days) ? addDays(values.invoice_date, days) : "";

  function choose(item: RateItem, change: Partial<LineChoice>): void {
    setChoices((current) => {
      const previous = current[item.id] ?? cardChoice(item);
      return { ...current, [item.id]: { ...previous, ...change } };
    });
  }

  function changeExtra(index: number, change: Partial<Extra>): void {
    setExtras((list) => list.map((extra, at) => (at === index ? { ...extra, ...change } : extra)));
  }

  function handleSubmit(event: FormEvent<HTMLFormElement>): void {
    event.preventDefault();
    const problems: Record<string, string> = {};
    if (!values.vessel_call_id) problems.vessel_call_id = "Choose the vessel call this invoice is for";
    if (chosen.length === 0 && filledExtras.length === 0) {
      problems.lines = "Choose at least one item, or add a charge of your own";
    }
    if (chosen.some(({ choice }) => !choice.description.trim())) {
      problems.lines = "Give every item a description, or go back to the rate card's wording";
    }
    if (extras.some((extra) => extra.description.trim() !== "" && !extra.unit_rate.trim())) {
      problems.lines = "Give every charge you have added a rate";
    }
    const typedDiscount = values.discount_amount.trim();
    if (typedDiscount && (!/^\d+(\.\d{1,2})?$/.test(typedDiscount) || discountCents <= 0)) {
      problems.discount = "Enter the discount as an amount, e.g. 1050 or 1050.00";
    } else if (discountCents > 0 && !values.discount_description.trim()) {
      problems.discount = "Say what the discount is for - it is printed";
    } else if (discountCents > subtotalCents) {
      problems.discount = "The discount cannot be more than the subtotal";
    }
    setMissing(problems);
    if (Object.keys(problems).length > 0) return;
    void run(() =>
      onSubmit({
        vessel_call_id: values.vessel_call_id,
        invoice_date: values.invoice_date,
        payment_days: Number.parseInt(values.payment_days, 10),
        bank_account_id: values.bank_account_id || null,
        remarks: values.remarks.trim(),
        lines: [
          ...chosen.map(({ item, choice }) => cardLine(item, choice)),
          ...filledExtras.map((extra) => ({
            qty: extra.qty.trim() || "1",
            description: extra.description.trim(),
            detail: extra.detail.trim(),
            unit_rate: extra.unit_rate.trim(),
            rate_group: extra.rate_group,
            ...(extra.item_no === undefined ? {} : { item_no: extra.item_no }),
          })),
        ],
        discount:
          discountCents > 0
            ? {
                description: values.discount_description.trim(),
                amount: values.discount_amount.trim(),
                note: values.discount_note.trim(),
              }
            : null,
        extra_notes: notesFromText(values.extra_notes),
      }),
    );
  }

  const callOptions: SelectOption[] = calls.map((call) => ({ value: call.id, label: callLabel(call) }));
  const bankOptions: SelectOption[] = [
    { value: "", label: "None yet - bank details follow on letterhead" },
    ...bankAccounts.map((bank) => ({
      value: bank.id,
      label: `${bank.label} - ${bank.bank} (${bank.currency})`,
    })),
  ];
  const lineError = missing.lines ?? errors.lines;

  return (
    <form onSubmit={handleSubmit} noValidate className="flex flex-col gap-6">
      <RequestError error={error} onReload={onReload} />
      <div className="grid gap-4 md:grid-cols-2">
        <SelectField
          id={`${id}-call`}
          label="Vessel call"
          value={values.vessel_call_id}
          onChange={(value) => set("vessel_call_id", value)}
          options={callOptions}
          placeholder={calls.length === 0 ? "No unbilled vessel calls" : "Choose a vessel call"}
          error={missing.vessel_call_id ?? errors.vessel_call_id}
          hint="Calls already on an invoice are not listed"
          className="md:col-span-2"
        />
        <TextField
          id={`${id}-date`}
          label="Invoice date"
          type="date"
          value={values.invoice_date}
          onChange={(value) => set("invoice_date", value)}
          error={errors.invoice_date}
          hint="On or after the vessel's departure"
        />
        <TextField
          id={`${id}-days`}
          label="Payment days"
          type="number"
          inputMode="numeric"
          value={values.payment_days}
          onChange={(value) => set("payment_days", value)}
          error={errors.payment_days}
          hint={dueDate ? `Due ${formatDate(dueDate)}` : undefined}
        />
        <SelectField
          id={`${id}-bank`}
          label="Bank account printed on the invoice"
          value={values.bank_account_id}
          onChange={(value) => set("bank_account_id", value)}
          options={bankOptions}
          error={errors.bank_account_id}
          className="md:col-span-2"
        />
      </div>

      <fieldset className="flex flex-col gap-4">
        <legend className="mb-1 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
          Items
        </legend>
        {(["A", "B"] as const).map((group) => {
          const groupItems = items.filter((item) => item.rate_group === group);
          if (groupItems.length === 0) return null;
          return (
            <div key={group} className="flex flex-col gap-2">
              <p className="text-sm font-medium text-primary">{GROUP_TITLES[group]}</p>
              <ul className="flex flex-col gap-2">
                {groupItems.map((item) => {
                  const choice = choices[item.id] ?? cardChoice(item);
                  return (
                    <li key={item.id} className="flex flex-col gap-2 rounded-md border bg-card p-3">
                      <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:gap-4">
                        <Checkbox
                          id={`${id}-item-${item.id}`}
                          label={`${item.item_no}. ${item.description}`}
                          hint={item.detail || undefined}
                          checked={choice.checked}
                          onChange={(checked) => choose(item, { checked })}
                          className="min-w-0 flex-1"
                        />
                        <div className="flex items-center justify-between gap-3 sm:justify-end">
                          {choice.checked && !choice.rewording && (
                            <Button
                              type="button"
                              size="sm"
                              variant="ghost"
                              onClick={() => choose(item, { rewording: true })}
                            >
                              Edit wording
                            </Button>
                          )}
                          <Input
                            type="number"
                            inputMode="decimal"
                            min="0"
                            step="any"
                            aria-label={`Quantity of item ${item.item_no}`}
                            value={choice.qty}
                            disabled={!choice.checked}
                            onChange={(event) => choose(item, { qty: event.target.value })}
                            className="w-24"
                          />
                          <span className="w-36 text-right text-sm font-medium tabular-nums">
                            {choice.checked
                              ? formatMoney(centsToAmount(lineCents(choice.qty, item.unit_rate)), currency)
                              : `${formatMoney(item.unit_rate, item.currency)} each`}
                          </span>
                        </div>
                      </div>
                      {choice.checked && choice.rewording && (
                        <div className="grid gap-2 sm:grid-cols-2">
                          <TextField
                            id={`${id}-item-${item.id}-description`}
                            label={`Item ${item.item_no} printed as`}
                            value={choice.description}
                            onChange={(value) => choose(item, { description: value })}
                            hint="For this invoice only; the rate card and its rate stay as they are"
                          />
                          <TextField
                            id={`${id}-item-${item.id}-detail`}
                            label={`Note under item ${item.item_no}`}
                            value={choice.detail}
                            onChange={(value) => choose(item, { detail: value })}
                          />
                          <div>
                            <Button
                              type="button"
                              size="sm"
                              variant="ghost"
                              onClick={() =>
                                choose(item, {
                                  description: item.description,
                                  detail: item.detail,
                                  rewording: false,
                                })
                              }
                            >
                              Use the rate card wording
                            </Button>
                          </div>
                        </div>
                      )}
                    </li>
                  );
                })}
              </ul>
            </div>
          );
        })}
        <div className="flex flex-col gap-2">
          <p className="text-sm font-medium text-primary">Agreed for this call only</p>
          <p className="text-xs text-muted-foreground">
            Work the client asked for that the rate card does not price yet - a COD declaration,
            a restow, a one-off check. It is numbered after the last item on their rate card and
            printed under Group B. If it comes round again, add it to the rate card instead.
          </p>
          <ul className="flex flex-col gap-2">
            {extras.map((extra, index) => (
              <li key={extra.key} className="flex flex-col gap-2 rounded-md border bg-card p-3">
                <div className="grid gap-2 sm:grid-cols-[1fr_5rem_8rem_auto] sm:items-end">
                  <TextField
                    id={`${id}-extra-${extra.key}-description`}
                    label="Charge"
                    value={extra.description}
                    onChange={(value) => changeExtra(index, { description: value })}
                    placeholder="COD declaration / restow generation"
                  />
                  <TextField
                    id={`${id}-extra-${extra.key}-qty`}
                    label="Qty"
                    inputMode="decimal"
                    value={extra.qty}
                    onChange={(value) => changeExtra(index, { qty: value })}
                  />
                  <TextField
                    id={`${id}-extra-${extra.key}-rate`}
                    label={`Rate (${currency})`}
                    inputMode="decimal"
                    value={extra.unit_rate}
                    onChange={(value) => changeExtra(index, { unit_rate: value })}
                  />
                  <div className="flex items-center justify-between gap-3 pb-1 sm:justify-end">
                    <span className="w-28 text-right text-sm font-medium tabular-nums">
                      {extra.unit_rate.trim()
                        ? formatMoney(centsToAmount(lineCents(extra.qty, extra.unit_rate)), currency)
                        : "\u2014"}
                    </span>
                    <Button
                      type="button"
                      size="sm"
                      variant="ghost"
                      onClick={() => setExtras((list) => list.filter((_, at) => at !== index))}
                    >
                      Remove
                    </Button>
                  </div>
                </div>
                <TextField
                  id={`${id}-extra-${extra.key}-detail`}
                  label="Note printed under it"
                  value={extra.detail}
                  onChange={(value) => changeExtra(index, { detail: value })}
                  placeholder="On-demand variation requested by the Client (outside Annexure B)"
                />
              </li>
            ))}
          </ul>
          <div>
            <Button
              type="button"
              size="sm"
              variant="outline"
              onClick={() => setExtras((list) => [...list, blankExtra()])}
            >
              <Plus className="h-3.5 w-3.5" aria-hidden />
              Add a charge
            </Button>
          </div>
        </div>
        {lineError && <p className="text-xs text-destructive">{lineError}</p>}
        <div className="flex flex-col gap-2">
          <p className="text-sm font-medium text-primary">Discount</p>
          <p className="text-xs text-muted-foreground">
            Leave the items at their rates and take the amount off here, e.g. an item waived for
            this call alone. The invoice prints it under the subtotal with a note calling it a
            one-time concession, so the rate card still stands.
          </p>
          <div className="grid gap-2 sm:grid-cols-[1fr_10rem]">
            <TextField
              id={`${id}-discount-description`}
              label="Printed as"
              value={values.discount_description}
              onChange={(value) => set("discount_description", value)}
              placeholder="24x7 planning support (Item 2) waived for this Vessel Call"
            />
            <TextField
              id={`${id}-discount-amount`}
              label={`Discount (${currency})`}
              inputMode="decimal"
              value={values.discount_amount}
              onChange={(value) => set("discount_amount", value)}
            />
          </div>
          <TextAreaField
            id={`${id}-discount-note`}
            label="Discount note"
            value={values.discount_note}
            onChange={(value) => set("discount_note", value)}
            hint={STANDARD_DISCOUNT_NOTE}
            rows={2}
          />
          {(missing.discount ?? errors.discount) && (
            <p className="text-xs text-destructive">{missing.discount ?? errors.discount}</p>
          )}
        </div>
        {discountCents > 0 && (
          <div className="flex flex-col gap-1 border-t pt-3 text-sm tabular-nums">
            <div className="flex justify-between">
              <span>Subtotal</span>
              <span>{formatMoney(centsToAmount(subtotalCents), currency)}</span>
            </div>
            <div className="flex justify-between">
              <span>Less discount</span>
              <span>({formatMoney(centsToAmount(discountCents), currency)})</span>
            </div>
          </div>
        )}
        <div className="flex items-center justify-between border-t pt-3">
          <span className="font-semibold">Total</span>
          <span className="text-lg font-semibold tabular-nums">
            {formatMoney(centsToAmount(totalCents), currency)}
          </span>
        </div>
        <p className="text-xs text-muted-foreground">
          Export of services: zero-rated, no GST is added.
        </p>
      </fieldset>

      <TextAreaField
        id={`${id}-extra-notes`}
        label="Additional notes"
        value={values.extra_notes}
        onChange={(value) => set("extra_notes", value)}
        error={errors.extra_notes}
        hint={EXTRA_NOTES_HINT}
        rows={2}
      />
      <TextAreaField
        id={`${id}-remarks`}
        label="Internal remarks"
        value={values.remarks}
        onChange={(value) => set("remarks", value)}
        error={errors.remarks}
        hint="For directors only - never printed"
        rows={2}
      />
      <FormActions saving={saving} submitLabel={submitLabel} onCancel={onCancel} />
    </form>
  );
}
